import type { AdminLanguage } from '@sirius/contracts/admin'
import type { i18n as I18n } from 'i18next'
import { createInstance } from 'i18next'
import { initReactI18next } from 'react-i18next'
import type { Catalog } from './catalogs'
import { EN_CATALOG, loadCatalog } from './catalogs'

/**
 * The panel's i18next instance: English loaded, every key missing in the
 * current language answered from it (per key, so a half-filled catalog shows
 * English for the rest). Other languages are added by `useLanguage`.
 */
export function createI18n(
  load: (language: AdminLanguage) => Promise<Catalog> = loadCatalog,
) {
  const i18n = createInstance()
  void i18n.use(initReactI18next).init({
    lng: 'en',
    fallbackLng: 'en',
    resources: { en: { translation: EN_CATALOG } },
    // a Refine key is a dotted path; the catalog nests it
    keySeparator: '.',
    interpolation: { escapeValue: false },
    returnNull: false,
    initAsync: false,
  })
  // the newest switch wins: a slower catalog of an earlier one must not
  // change the language after it
  let latest = 0
  return Object.assign(i18n, {
    /**
     * Shows the panel in `language`: loads its catalog first when it is not
     * there yet, so the switch never flashes keys or English.
     */
    async useLanguage(language: AdminLanguage) {
      latest += 1
      const mine = latest
      if (!i18n.hasResourceBundle(language, 'translation')) {
        const catalog = await load(language)
        i18n.addResourceBundle(language, 'translation', catalog)
      }
      if (mine === latest) {
        await i18n.changeLanguage(language)
      }
    },
  })
}

export type PanelI18n = I18n & ReturnType<typeof createI18n>
